/**
 * Comprehensive UI Logging System
 * Tracks component rendering, loading states, errors, and performance
 */

export type UIErrorLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

export interface UILogEntry {
  timestamp: string;
  level: UIErrorLevel;
  component: string;
  action: string;
  details?: Record<string, any>;
  error?: Error;
  stackTrace?: string;
  duration?: number;
  userId?: string;
}

class UILogger {
  private logs: UILogEntry[] = [];
  private maxLogs = 500;
  private isDevMode = import.meta.env.DEV;
  private componentStates: Map<string, any> = new Map();

  /**
   * Log UI component rendering
   */
  logComponentRender(
    component: string,
    props?: Record<string, any>,
    userId?: string
  ) {
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: 'debug',
      component,
      action: 'COMPONENT_RENDER',
      details: {
        props: props ? JSON.stringify(props, null, 2) : undefined,
      },
      userId,
    };
    this.addLog(entry);
    if (this.isDevMode) {
      console.log(`[UI:${component}] Rendering`, props);
    }
  }

  /**
   * Log component unmounting
   */
  logComponentUnmount(component: string, userId?: string) {
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: 'debug',
      component,
      action: 'COMPONENT_UNMOUNT',
      userId,
    };
    this.addLog(entry);
    if (this.isDevMode) {
      console.log(`[UI:${component}] Unmounting`);
    }
  }

  /**
   * Log data loading states
   */
  logDataLoading(
    component: string,
    dataType: string,
    state: 'START' | 'SUCCESS' | 'FAILURE',
    details?: Record<string, any>,
    userId?: string
  ) {
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: state === 'FAILURE' ? 'error' : 'info',
      component,
      action: `DATA_LOADING_${state}`,
      details: {
        dataType,
        ...details,
      },
      userId,
    };
    this.addLog(entry);

    const emoji =
      state === 'START' ? '⏳' : state === 'SUCCESS' ? '✅' : '❌';
    const level =
      state === 'FAILURE' ? 'error' : state === 'SUCCESS' ? 'log' : 'debug';

    console[level as any](
      `[UI:${component}] ${emoji} Data Loading ${state}: ${dataType}`,
      details
    );
  }

  /**
   * Log state changes
   */
  logStateChange(
    component: string,
    stateName: string,
    oldValue: any,
    newValue: any,
    userId?: string
  ) {
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: 'debug',
      component,
      action: 'STATE_CHANGE',
      details: {
        stateName,
        oldValue: JSON.stringify(oldValue),
        newValue: JSON.stringify(newValue),
      },
      userId,
    };
    this.addLog(entry);

    if (this.isDevMode) {
      console.log(
        `[UI:${component}] State Changed: ${stateName}`,
        { old: oldValue, new: newValue }
      );
    }
  }

  /**
   * Log user interactions
   */
  logUserInteraction(
    component: string,
    action: string,
    details?: Record<string, any>,
    userId?: string
  ) {
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: 'info',
      component,
      action: `USER_ACTION_${action}`,
      details,
      userId,
    };
    this.addLog(entry);
    console.log(`[UI:${component}] User Action: ${action}`, details);
  }

  /**
   * Log UI errors with context
   */
  logUIError(
    component: string,
    action: string,
    error: Error | string,
    details?: Record<string, any>,
    userId?: string
  ) {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: 'error',
      component,
      action: `ERROR_${action}`,
      details: {
        ...details,
        errorMessage: errorObj.message,
      },
      error: errorObj,
      stackTrace: errorObj.stack,
      userId,
    };
    this.addLog(entry);
    console.error(`[UI:${component}] ERROR in ${action}:`, {
      message: errorObj.message,
      stack: errorObj.stack,
      details,
    });
  }

  /**
   * Log critical failures
   */
  logCriticalError(
    component: string,
    action: string,
    error: Error | string,
    details?: Record<string, any>,
    userId?: string
  ) {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: 'critical',
      component,
      action: `CRITICAL_ERROR_${action}`,
      details: {
        ...details,
        errorMessage: errorObj.message,
      },
      error: errorObj,
      stackTrace: errorObj.stack,
      userId,
    };
    this.addLog(entry);
    console.error(`[CRITICAL:${component}] FAILURE in ${action}:`, {
      message: errorObj.message,
      stack: errorObj.stack,
      details,
    });

    // Trigger telemetry or alerting here
    this.reportCriticalError(entry);
  }

  /**
   * Log async operation timing
   */
  async logAsyncOperation<T>(
    component: string,
    operationName: string,
    operation: () => Promise<T>,
    userId?: string
  ): Promise<T> {
    const startTime = performance.now();
    this.logUserInteraction(component, `ASYNC_START_${operationName}`, {}, userId);

    try {
      const result = await operation();
      const duration = performance.now() - startTime;

      const entry: UILogEntry = {
        timestamp: new Date().toISOString(),
        level: 'info',
        component,
        action: `ASYNC_SUCCESS_${operationName}`,
        duration,
        userId,
      };
      this.addLog(entry);
      console.log(
        `[UI:${component}] ✅ Async Operation Completed: ${operationName} (${Math.round(duration)}ms)`
      );

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      const errorObj = error instanceof Error ? error : new Error(String(error));

      const entry: UILogEntry = {
        timestamp: new Date().toISOString(),
        level: 'error',
        component,
        action: `ASYNC_FAILURE_${operationName}`,
        duration,
        error: errorObj,
        stackTrace: errorObj.stack,
        userId,
      };
      this.addLog(entry);
      console.error(
        `[UI:${component}] ❌ Async Operation Failed: ${operationName}`,
        errorObj
      );

      throw error;
    }
  }

  /**
   * Log component mount/lifecycle issues
   */
  logMountIssue(
    component: string,
    issue: string,
    details?: Record<string, any>,
    userId?: string
  ) {
    const entry: UILogEntry = {
      timestamp: new Date().toISOString(),
      level: 'warn',
      component,
      action: 'MOUNT_ISSUE',
      details: {
        issue,
        ...details,
      },
      userId,
    };
    this.addLog(entry);
    console.warn(`[UI:${component}] Mount Issue: ${issue}`, details);
  }

  /**
   * Track component state in memory
   */
  trackComponentState(component: string, state: Record<string, any>) {
    this.componentStates.set(component, {
      ...this.componentStates.get(component),
      ...state,
      lastUpdated: new Date().toISOString(),
    });
  }

  /**
   * Get component state tracking
   */
  getComponentState(component: string) {
    return this.componentStates.get(component);
  }

  /**
   * Add log entry with size management
   */
  private addLog(entry: UILogEntry) {
    this.logs.push(entry);

    // Trim logs if exceeding max size
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Get all logs
   */
  getLogs(filter?: {
    component?: string;
    level?: UIErrorLevel;
    since?: Date;
  }): UILogEntry[] {
    return this.logs.filter((log) => {
      if (filter?.component && log.component !== filter.component)
        return false;
      if (filter?.level && log.level !== filter.level) return false;
      if (filter?.since && new Date(log.timestamp) < filter.since)
        return false;
      return true;
    });
  }

  /**
   * Get error logs only
   */
  getErrorLogs(): UILogEntry[] {
    return this.logs.filter((log) => log.level === 'error' || log.level === 'critical');
  }

  /**
   * Export logs for debugging
   */
  exportLogs(format: 'json' | 'csv' = 'json'): string {
    if (format === 'json') {
      return JSON.stringify(this.logs, null, 2);
    }

    // CSV format
    const headers = ['timestamp', 'level', 'component', 'action', 'details', 'error'];
    const rows = this.logs.map((log) => [
      log.timestamp,
      log.level,
      log.component,
      log.action,
      JSON.stringify(log.details || {}),
      log.error?.message || '',
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csv;
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
    this.componentStates.clear();
  }

  /**
   * Report critical errors (can be extended to send to monitoring service)
   */
  private reportCriticalError(entry: UILogEntry) {
    if (this.isDevMode) {
      console.group('🚨 CRITICAL ERROR REPORT');
      console.log('Timestamp:', entry.timestamp);
      console.log('Component:', entry.component);
      console.log('Action:', entry.action);
      console.log('Details:', entry.details);
      console.log('Error:', entry.error);
      console.log('Stack:', entry.stackTrace);
      console.groupEnd();
    }

    // TODO: Send to error tracking service (Sentry, etc.)
  }

  /**
   * Generate performance report
   */
  generatePerformanceReport(): {
    totalLogs: number;
    errors: number;
    warnings: number;
    averageAsyncDuration: number;
    slowestOperations: Array<{ action: string; duration: number }>;
  } {
    const asyncLogs = this.logs.filter((log) => log.action.includes('ASYNC') && log.duration);
    const slowest = asyncLogs
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))
      .slice(0, 5);

    return {
      totalLogs: this.logs.length,
      errors: this.logs.filter((log) => log.level === 'error').length,
      warnings: this.logs.filter((log) => log.level === 'warn').length,
      averageAsyncDuration:
        asyncLogs.length > 0
          ? asyncLogs.reduce((sum, log) => sum + (log.duration || 0), 0) /
            asyncLogs.length
          : 0,
      slowestOperations: slowest.map((log) => ({
        action: log.action,
        duration: log.duration || 0,
      })),
    };
  }
}

// Export singleton instance
export const uiLogger = new UILogger();

// Make available globally in dev mode
if (import.meta.env.DEV) {
  (window as any).uiLogger = uiLogger;
}
